import { Link, useNavigate } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';

import type { GapDraftView } from '@momentum/types';
import { GapDraftReviewConfidence } from '@momentum/types';

import {
  useKeptGapDrafts,
  useSetGapDraftReview,
} from '@/shared/hooks/useGapReview';
import { useGapReviewData } from '../gap-review-context';
import { titleOf } from '../lib/derive-title';
import { dateTime, pluralize } from '../lib/format';
import { DROP_STATUS } from '../lib/queue';
import { DEFAULT_READER_VIEW } from '../lib/reader-view';
import { InlineMarkdown } from './InlineMarkdown';
import { ReadinessChecklist } from './ReadinessChecklist';
import { RunPicker } from './RunPicker';

/** The search params that open one draft in the reader from outside the queue. */
function readerSearchFor(draft: GapDraftView) {
  // `filter: 'all'` rather than the reviewer's last filter: a kept draft opened
  // from here is not in the `todo` list, and landing on a queue that does not
  // contain the draft being read is disorienting.
  return {
    execution: draft.executionId,
    filter: 'all' as const,
    draft: draft._id,
    view: DEFAULT_READER_VIEW,
  };
}

/**
 * A tone per level, so the strength of a verdict is readable without reading.
 * Green through amber to rose is the one gradient nobody has to be taught.
 */
const CONFIDENCE_TONE: Record<GapDraftReviewConfidence, string> = {
  [GapDraftReviewConfidence.HIGH]: 'emerald',
  [GapDraftReviewConfidence.MEDIUM]: 'amber',
  [GapDraftReviewConfidence.LOW]: 'rose',
};

/**
 * How sure the reviewer was of the verdict on this card.
 *
 * It sits where the validator's verdict used to, which is the slot the eye
 * reaches after the detector: on a page about human decisions the reviewer's own
 * confidence is what a second reader needs, and the validator's adjudication is
 * already folded into the headline below.
 *
 * It renders on every decided draft, including the ones with nothing on record
 * — seeded drafts, and any decided before the field existed. Saying "no
 * confidence recorded" costs a chip and removes the ambiguity of a blank space,
 * which otherwise reads either as "nobody was asked" or as "the tag is broken",
 * with no way to tell which from the card.
 */
function ConfidenceChip({ draft }: { draft: GapDraftView }) {
  const { reviewConfidence } = draft.review;

  if (reviewConfidence === undefined) {
    return (
      <span className="gr-chip" data-tone="muted">
        no confidence recorded
      </span>
    );
  }

  return (
    <span className="gr-chip" data-tone={CONFIDENCE_TONE[reviewConfidence]}>
      {reviewConfidence} confidence
    </span>
  );
}

interface KeptCardProps {
  draft: GapDraftView;
  onReturnToQueue: (draft: GapDraftView) => void;
}

function KeptCard({ draft, onReturnToQueue }: KeptCardProps) {
  const validation = draft.source.validation;

  return (
    <article className="gr-keptcard" data-status="keep">
      <div>
        <h2 className="gr-keptcard-title">
          <InlineMarkdown text={titleOf(draft)} />
        </h2>
        <div className="gr-keptcard-meta">
          <span className="gr-chip">{draft.scopeName}</span>
          {/* The list spans every run, so each card has to say which one it came
              out of — and the run audit is where that question is answered, so
              the chip goes there rather than merely stating an id. */}
          <Link
            className="gr-chip gr-chip-link"
            to="/gap-review/run/$executionId"
            params={{ executionId: draft.executionId }}
            title="Open this run's audit"
          >
            {draft.executionId}
          </Link>
          <span className="gr-chip">
            {draft.source.metadata.detector} · {draft.source.claimType}
          </span>
          <ConfidenceChip draft={draft} />
          <span className="gr-chip">
            kept {dateTime(draft.review.decidedAt)}
          </span>
          {draft.review.comments.length > 0 && (
            <span className="gr-chip">
              {pluralize(draft.review.comments.length, 'comment')}
            </span>
          )}
        </div>
        {/* The validator's one-line outcome, which is what a second reviewer
            reads first. With no verdict there is nothing to summarise, so the
            finder's own claim stands in and is labelled as such. */}
        <p className="gr-cand-claim">
          {validation?.headline ?? `${draft.source.claim} — finder's claim`}
        </p>
        <div className="gr-keptcard-acts gr-stack-top">
          {/* First, because this pass is about what the customer would be
              served — the reader is where the reviewer already was. */}
          <Link
            className="gr-btn-ghost"
            to="/gap-review/preview/$draftId"
            params={{ draftId: draft._id }}
            // The run travels with the link: the preview reads the draft out of
            // the run the surface has loaded, and arriving without one would
            // land it on the latest run, which need not hold this draft.
            search={{ from: 'final' as const, execution: draft.executionId }}
          >
            Preview in app
          </Link>
          <Link
            className="gr-btn-ghost"
            to="/gap-review"
            search={readerSearchFor(draft)}
          >
            Open in the reader
          </Link>
          <button
            className="gr-btn-ghost"
            type="button"
            onClick={() => onReturnToQueue(draft)}
          >
            Return to the queue
          </button>
        </div>
      </div>

      <ReadinessChecklist draft={draft} />
    </article>
  );
}

interface DroppedCardProps {
  draft: GapDraftView;
  onReturnToQueue: (draft: GapDraftView) => void;
}

function DroppedCard({ draft, onReturnToQueue }: DroppedCardProps) {
  const lastComment = draft.review.comments.at(-1);

  return (
    <article className="gr-keptcard" data-status="drop">
      <div>
        <h2 className="gr-keptcard-title">
          <InlineMarkdown text={titleOf(draft)} />
        </h2>
        <div className="gr-keptcard-meta gr-keptcard-meta-flush">
          <span className="gr-chip">{draft.scopeName}</span>
          <span className="gr-chip">
            {draft.source.metadata.detector} · {draft.source.claimType}
          </span>
          <ConfidenceChip draft={draft} />
          <span className="gr-chip">
            dropped {dateTime(draft.review.decidedAt)}
          </span>
        </div>
        {lastComment === undefined ? (
          <p className="gr-cand-claim gr-cand-claim-faint gr-stack-top-sm">
            No reason recorded.
          </p>
        ) : (
          <p className="gr-cand-claim gr-stack-top-sm">“{lastComment.text}”</p>
        )}
      </div>
      <div className="gr-keptcard-acts gr-acts-top-align">
        <Link
          className="gr-btn-ghost"
          to="/gap-review"
          search={readerSearchFor(draft)}
        >
          Open
        </Link>
        <button
          className="gr-btn-ghost"
          type="button"
          onClick={() => onReturnToQueue(draft)}
        >
          Restore
        </button>
      </div>
    </article>
  );
}

/**
 * The second pass over kept gaps, and the record of the dropped ones.
 *
 * Keeping a gap in the queue is not publishing it — it moves the draft here, and
 * a different person decides what deserves to reach the `gaps` collection. That
 * decision cannot currently be carried out, and the page says why per draft
 * rather than hiding the control: see `ReadinessChecklist`.
 *
 * The two lists are scoped differently, deliberately.
 *
 * Kept crosses every run, because this pass is not about runs: a gap is here
 * because somebody kept it, and which execution found it is an implementation
 * detail of that. Splitting the list by run would make the second reviewer walk
 * every execution to find the handful waiting for them. It has its own endpoint
 * — the list only grows by a verdict at a time, so it is nothing like fetching
 * the corpus.
 *
 * Dropped stays per run and names the run it is showing, because it is a record
 * rather than a working set: it is read when someone asks what a PARTICULAR run
 * threw away, and cross-run it would be an ever-growing list of decisions
 * already made. It reads the drafts the surface has already loaded, so the run
 * it shows is the one selected everywhere else — picking another here moves that
 * selection, exactly as the queue's own picker does.
 */
export function FinalReviewView() {
  const { drafts, executions, executionId } = useGapReviewData();
  const navigate = useNavigate();
  const setReview = useSetGapDraftReview();

  const {
    data: keptData,
    isPending: areKeptDraftsPending,
    error: keptDraftsError,
  } = useKeptGapDrafts();
  const kept = keptData?.drafts ?? [];

  const dropped = drafts.filter((draft) => draft.review.status === DROP_STATUS);

  // Both lists send a draft back to PENDING, which is the same write the queue's
  // verdict toggle makes; it drops the card out of the list it was in.
  const returnToQueue = (draft: GapDraftView) => {
    setReview.mutate(
      { draftId: draft._id, status: null },
      {
        onError: (error) =>
          toast.error(`Could not reopen “${titleOf(draft)}”`, {
            description: error.message,
          }),
      }
    );
    toast('Back in the review queue', { description: titleOf(draft) });
  };

  return (
    <div className="gr-view gr-page">
      <div className="gr-page-in">
        <h1 className="gr-page-h1">Final review</h1>
        <p className="gr-page-lede">
          Keeping a gap in the queue moves it here. This is the second pass — a
          different person walks the kept list and decides what deserves to
          reach the <code>gaps</code> collection. The kept list covers every
          run: a gap is here because somebody kept it, not because of the run it
          came out of. The dropped record below it is per run, and says which.
          <br />
          <br />
          Publishing is not wired up, and not just unbuilt: <code>
            Gap
          </code>{' '}
          requires <code>externalRef</code>, <code>title</code>, a complete{' '}
          <code>projectOverview</code> and a numeric 0–1 <code>severity</code>,
          and no draft has all four. Each card below shows which of them it can
          actually satisfy.
        </p>

        <div className="gr-note gr-note-page">
          <TriangleAlert />
          <div>
            <strong>There is no publish gate downstream of this one.</strong>{' '}
            The published read path filters on company and{' '}
            <code>isDeleted</code> only — no status, no draft flag. Anything
            written into <code>gaps</code> is immediately live on the
            customer-facing deal-intelligence page, which is why promotion stays
            out of scope for v1.
          </div>
        </div>

        <div className="gr-rule gr-rule-flush">
          <span>
            Kept · {areKeptDraftsPending ? '…' : kept.length} · all runs
          </span>
          <i />
        </div>

        {areKeptDraftsPending && (
          <div className="gr-empty">
            <b>Loading</b>Fetching every kept gap.
          </div>
        )}

        {keptDraftsError && (
          <div className="gr-empty">
            <b>Could not load the kept gaps</b>
            {keptDraftsError.message}
          </div>
        )}

        {!areKeptDraftsPending &&
          !keptDraftsError &&
          (kept.length === 0 ? (
            <div className="gr-empty">
              <b>Nothing kept yet</b>Keep a gap in the review queue and it
              appears here.
            </div>
          ) : (
            kept.map((draft) => (
              <KeptCard
                key={draft._id}
                draft={draft}
                onReturnToQueue={returnToQueue}
              />
            ))
          ))}

        <div className="gr-rule">
          <span>Dropped · {dropped.length} · one run</span>
          <i />
        </div>
        <p className="gr-page-lede gr-page-lede-quiet">
          Kept for the record. A drop is a judgement someone may want to revisit
          — the reason usually lives in the comment, not the status. Pick the
          run whose drops you want to see; it is the same selection the queue
          uses.
        </p>

        <div className="gr-runrow">
          <span className="gr-runrow-label">Run</span>
          <div className="gr-runrow-pick">
            <RunPicker
              executions={executions}
              value={executionId}
              onChange={(nextExecutionId) =>
                void navigate({
                  to: '/gap-review/final',
                  search: { execution: nextExecutionId },
                })
              }
            />
          </div>
        </div>

        {dropped.length === 0 ? (
          <div className="gr-empty">
            <b>Nothing dropped</b>No draft of this run has been dropped.
          </div>
        ) : (
          dropped.map((draft) => (
            <DroppedCard
              key={draft._id}
              draft={draft}
              onReturnToQueue={returnToQueue}
            />
          ))
        )}
      </div>
    </div>
  );
}
